import React, { useEffect, useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import { ActionsType } from "src/interfaces/Common";
import { KeyURL, MethodType } from "src/services/type";
import { downloadFile, uploadFile } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useParams } from "react-router-dom";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { disableRowBasedOnDisableIf } from "@utils/utilsTable";
import { useAuth } from "@context/auth";
import ButtonBackPage from "@components/ButtonBackPage";
import { useFilters } from "@context/filters";

const Contract: React.FC = () => {
  const { id } = useParams();
  const [idContractDeleted, setidContractDeleted] = useState<string>();
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [openModalDelete, setOpenModalDelete] = useState(false);

  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["contracts"]}`;

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getContracts}`,
      PagesID["contracts"],
      filters[filterId],
    );

  const { t } = useTranslation(["contracts-page"]);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const { trigger: triggerContractCenter } = useSWRMutation(
    `${ServicesURL.postContracts}/${idContractDeleted}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.DELETE);
    },
  );

  const handleDelete = React.useCallback(async () => {
    if (idContractDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerContractCenter();
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idContractDeleted),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setidContractDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idContractDeleted, mutate, triggerContractCenter]);

  useEffect(() => {
    if (!idContractDeleted) return;
  }, [idContractDeleted]);

  const dataFormat = useMemo(() => {
    if (!dataTable?.output) return [];

    return dataTable.output.map((el: any) => {
      if (el.supplier) {
        el.supplier.label = el.supplier.business_name;
      }

      if (el.account) {
        el.account.label = el.account.sap_number;
      }

      return el;
    });
  }, [dataTable?.output]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      return columns;
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (id: string) => {
              setidContractDeleted(id);
              setOpenModalDelete(true);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    //FUNZIONE PER ABILITAZIONE/ DISABILITAZIONE CAMPI IN BASE A VALORI DELLA ROW
    configRowNew = configRowNew.map((el: ActionsType) => {
      if (el.type === "delete" && !row.original?.deletable) {
        return { ...el, disabled: true };
      }
      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "upload": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
              types: ["upload", "download", "template"],
              onDownload: (el) =>
                downloadFile(
                  `${ServicesURL[action.service as KeyURL]}/download${
                    el ? `/${el}` : ""
                  }?corporate_id=1&type=LEARNER` || "",
                ),
              onUpload: (formData: FormData) => {
                return uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                  formData,
                );
              },
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        case "visibility": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${
                ServicesURL[action.service as KeyURL]
              }?corporate_id=1`,
              types: ["upload", "download", "template"],
              onDownload: (el) =>
                downloadFile(
                  `${ServicesURL[action.service as KeyURL]}/?corporate_id=1` ||
                    "",
                ),
              onUpload: (formData: FormData) =>
                uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                  formData,
                ),
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-contracts.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID["contracts"]}
            data={dataFormat || []}
            columns={filteredColumns || []}
            configurationTopToolbar={actionsTopToolbar}
            renderRowActions={customRenderRowActions}
            totalPages={dataTable?.metadata?.total_elements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
      </Container>

      <ModalConfirmsCst
        open={!!openModalDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalDelete(!openModalDelete);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={openUpload?.labelTranslate || ""}
        data={openUpload}
      />
    </AnimationFadeIn>
  );
};

export default Contract;
